import {
  type JSX,
  Show,
  createSignal,
  mergeProps,
  onCleanup,
  onMount,
} from "solid-js";
import { Portal } from "solid-js/web";

import { getNextZIndex } from "@/components/Dialogs";
import { useDialogContext } from "@/components/Dialogs/DialogContext";
import Icon from "@/components/Icon";
import { overFlowFlagManager } from "@/lib/OverFlowFlag";
import { pubSub } from "@/lib/PubSub";
import { isMobile } from "@/lib/isMobile";
import { cn } from "@/lib/tailwindcssTool";
import "@/styles/motion/fade.css";
import { PubSubEvents } from "@/types/PubSub";

import "./popup.css";

interface PopupProps {
  show: boolean;
  onClose?: () => void;
  children: JSX.Element;
  headerIcon?: JSX.Element;
  title?: string;
  contentClass?: string;
  withHeader?: boolean;
  withPadding?: boolean;
  direction?: "top" | "bottom";
  zIndex?: number;
  typeFlag?: boolean; // 默认为true使用弹窗类型关闭
  offset?: number; // 距离顶部或底部的偏移量，单位px
  ref?: (ref: { handleClose: () => void }) => void;
}

export default function Popup(props: PopupProps) {
  const dialogType = useDialogContext(); // 从 Context 获取弹窗类型
  const mergedProps = mergeProps(
    {
      withHeader: true,
      withPadding: true,
      direction: "bottom",
      offset: 0,
      typeFlag: true,
    },
    props,
  );
  const [isClosing, setIsClosing] = createSignal(false);
  const handleClose = () => {
    if (isClosing()) return;
    setIsClosing(true);
  };

  // 暴露 handleClose 方法给父组件
  if (mergedProps.ref) {
    mergedProps.ref({ handleClose });
  }

  onMount(() => {
    if (mergedProps.show) {
      overFlowFlagManager.addFlag();
    }
    if (dialogType && mergedProps.typeFlag) {
      const unsubscribe = pubSub.subscribe(
        PubSubEvents.StartCloseAnimation,
        (type) => {
          // 当接收到开始关闭动画事件时，触发关闭动画
          if (type === dialogType) {
            handleClose();
          }
        },
      );
      onCleanup(unsubscribe);
    }
  });
  onCleanup(() => {
    overFlowFlagManager.removeFlag();
  });
  const handleAnimationEnd = (e: AnimationEvent) => {
    const closeAnimationName =
      mergedProps.direction === "top" ? "slideUpToTop" : "slideDownPopup";

    if (e.animationName === closeAnimationName && isClosing()) {
      if (dialogType && mergedProps.typeFlag) {
        pubSub.publish(PubSubEvents.CloseDialog, dialogType);
      }
      setIsClosing(false);
      mergedProps.onClose?.();
    }
  };
  return (
    <Show when={mergedProps.show || isClosing()} fallback={<></>}>
      <Portal>
        <div
          class={cn(
            "overlay",
            mergedProps.direction === "top" ? "overlay-top" : "overlay-bottom",
          )}
          style={{
            "z-index": mergedProps.zIndex ?? getNextZIndex(),
            [mergedProps.direction === "top" ? "top" : "bottom"]:
              `${mergedProps.offset}px`,
          }}
        >
          <div
            class={`absolute bottom-0 left-0 right-0 top-0 bg-black/50 ${isClosing() ? "fade-out" : "fade-in"}`}
            onClick={handleClose}
          ></div>
          <div
            class={cn(
              "drawer",
              mergedProps.direction === "top" ? "drawer-top" : "drawer-bottom",
              isClosing() && mergedProps.direction === "top" && "slideUp",
              isClosing() && mergedProps.direction === "bottom" && "slideDown",
              isClosing() ? "fade-out" : "fade-in",
            )}
            onClick={(e) => e.stopPropagation()}
            onAnimationEnd={handleAnimationEnd}
          >
            <Show when={mergedProps.withHeader} fallback={<></>}>
              <div class={cn("header", isMobile() && "header_h5 shrink-0")}>
                <div class="title" innerHTML={mergedProps.title}></div>
                <Show
                  when={!mergedProps.headerIcon}
                  fallback={mergedProps.headerIcon}
                >
                  <div class="closeButton" onClick={handleClose}>
                    <Icon
                      name="general/xx2"
                      class="text-general-light size-[11.65px]"
                    />
                  </div>
                </Show>
              </div>
            </Show>
            <div
              class={cn(
                "w-full overflow-y-auto",
                mergedProps.withPadding && "px-[12px] pb-[20px]",
                mergedProps.contentClass,
              )}
            >
              {mergedProps.children}
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
